/**
 * @documental
 * @export Logo
 * @import
 * import { Logo } from "capitalrx-components-2.0/lib/general";
 */

import { FC, ReactNode, RefCallback } from "react";
import classNames from "classnames";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Tooltip } from "../Tooltip";
import { LogoName } from "./LogoName";
import { getLogoComponent } from "./getLogoComponent";
import styles from "./styles.module.scss";

/**
 * The `Logo` component properties.
 */

export interface LogoProps extends BaseProps {
  /**
   * The logo name. See below for a list of available logo and their names.
   */
  readonly name: LogoName;

  /**
   * The logo tooltip.
   */
  readonly tooltip?: ReactNode;

  /**
   * The ARIA label for the Logo, used for accessibility.
   */
  readonly "aria-label"?: string;
}

/**
 * Displays a scalable logo with the specified size and color.
 */
export const Logo: FC<LogoProps> = (props) => {
  const { name, tooltip, "aria-label": ariaLabel } = props;

  if (name === "none") return <></>;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.Logo,
      "logo",
      {
        "has-tooltip": !!tooltip,
      },
    ],
    testIdPrefix: "logo",
  });

  const Component = getLogoComponent(name);

  let logoName: string | null = null;
  if (name.includes("judi")) {
    logoName = "Judi";
  } else if (name.includes("caprx")) {
    logoName = "CapRx";
  }

  const computedAriaLabel = ariaLabel || `${logoName} logo`;

  const content = (ref?: RefCallback<HTMLElement>) => (
    <span
      ref={ref}
      {...baseHtmlProps}
      aria-label={computedAriaLabel}
      role="img"
      data-testid={`test__${logoName}_logo`}
    >
      <span className={classNames(styles.LogoContainer, "logo-container")}>
        {
          Component !== null && <Component
            aria-labelledby={name}
            id={name}
            title={computedAriaLabel} />
        }
      </span>
    </span>
  );

  return (
    <>
      {tooltip ? <Tooltip content={tooltip}>{content}</Tooltip> : content()}
    </>
  );
};
